import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

// Persist key
const STORAGE_KEY = 'appstar_theme_mode';

const ThemeModeContext = createContext({
  isDarkMode: false,
  hasUserOverride: false,
  toggleTheme: () => {},
  setDarkMode: () => {},
  clearOverride: () => {}
});

export const ThemeModeProvider = ({ children }) => {
  // Initial value: system preference unless user previously chose
  const getInitial = () => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (stored === 'dark') return true;
    if (stored === 'light') return false;
    // Fallback to system preference
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  };

  const [isDarkMode, setIsDarkMode] = useState(getInitial);
  const [hasUserOverride, setHasUserOverride] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) != null; } catch { return false; }
  });

  // Keep in sync with system changes if user has NOT overridden
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      if (!hasUserOverride) setIsDarkMode(e.matches);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [hasUserOverride]);

  // Reflect mode on document root for Tailwind / CSS selectors
  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) root.classList.add('dark'); else root.classList.remove('dark');
  }, [isDarkMode]);

  // Persist only when there is an explicit user override; otherwise keep storage empty
  useEffect(() => {
    try {
      if (hasUserOverride) {
        localStorage.setItem(STORAGE_KEY, isDarkMode ? 'dark' : 'light');
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {}
  }, [isDarkMode, hasUserOverride]);

  const toggleTheme = useCallback(() => {
    setHasUserOverride(true);
    setIsDarkMode((d) => !d);
  }, []);
  const setDarkMode = useCallback((on) => {
    setHasUserOverride(true);
    setIsDarkMode(!!on);
  }, []);
  const clearOverride = useCallback(() => {
    setHasUserOverride(false);
    // Snap back to current system preference immediately
    try {
      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(!!prefersDark);
    } catch {}
  }, []);

  return (
    <ThemeModeContext.Provider value={{ isDarkMode, hasUserOverride, toggleTheme, setDarkMode, clearOverride }}>
      {children}
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => useContext(ThemeModeContext);
